import { useEffect } from 'react';

interface MetaTagData {
  title?: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
}

export const useDynamicMeta = (metaData: MetaTagData) => {
  useEffect(() => {
    const updateMetaTag = (selector: string, content: string, attribute: string = 'content') => {
      let element = document.querySelector(selector) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        if (selector.includes('property')) {
          element.setAttribute('property', selector.replace('meta[property="', '').replace('"]', ''));
        } else if (selector.includes('name')) {
          element.setAttribute('name', selector.replace('meta[name="', '').replace('"]', ''));
        }
        document.head.appendChild(element);
      }
      element.setAttribute(attribute, content);
    };

    // Update document title
    if (metaData.title) {
      document.title = metaData.title;
    }

    // Update meta description
    if (metaData.description) {
      updateMetaTag('meta[name="description"]', metaData.description);
    }

    // Update Open Graph tags
    if (metaData.ogTitle) {
      updateMetaTag('meta[property="og:title"]', metaData.ogTitle);
    }
    
    if (metaData.ogDescription) {
      updateMetaTag('meta[property="og:description"]', metaData.ogDescription);
    }
    
    if (metaData.ogImage) {
      updateMetaTag('meta[property="og:image"]', metaData.ogImage);
    }
    
    if (metaData.ogUrl) {
      updateMetaTag('meta[property="og:url"]', metaData.ogUrl);
    }

    // Update Twitter Card tags
    if (metaData.twitterTitle) {
      updateMetaTag('meta[name="twitter:title"]', metaData.twitterTitle);
    }
    
    if (metaData.twitterDescription) {
      updateMetaTag('meta[name="twitter:description"]', metaData.twitterDescription);
    }
    
    if (metaData.twitterImage) {
      updateMetaTag('meta[name="twitter:image"]', metaData.twitterImage);
    }

    // Cleanup function to reset to defaults when component unmounts
    return () => {
      document.title = 'Neo Prism Academy - Advanced Corporate Learning Platform';
      updateMetaTag('meta[name="description"]', 'Advanced corporate learning platform with AI-powered personalized learning paths, professional certifications, and team collaboration tools');
      updateMetaTag('meta[property="og:title"]', 'Neo Prism Academy');
      updateMetaTag('meta[property="og:description"]', 'Advanced corporate learning platform with AI-powered personalized learning paths, professional certifications, and team collaboration tools');
      updateMetaTag('meta[property="og:url"]', 'https://neoprismacademy.com/');
    };
  }, [metaData]);
};

export const generateCertificateMeta = (
  userName: string,
  courseTitle: string,
  certificateNumber: string,
  verificationHash?: string
): MetaTagData => {
  const baseUrl = 'https://neoprismacademy.com';
  const verificationUrl = verificationHash 
    ? `${baseUrl}/verify/${verificationHash}`
    : `${baseUrl}/certificate/${certificateNumber}`;

  return {
    title: `${userName} - ${courseTitle} Certificate | Neo Prism Academy`,
    description: `${userName} has successfully completed ${courseTitle} and earned a verified certificate from Neo Prism Academy. Certificate #${certificateNumber}`,
    ogTitle: `🎓 ${userName} earned a certificate in ${courseTitle}`,
    ogDescription: `Congratulations to ${userName} for completing ${courseTitle} at Neo Prism Academy! View their verified achievement certificate.`,
    ogImage: `${baseUrl}/certificate-og-image.png`,
    ogUrl: verificationUrl,
    twitterTitle: `🎓 ${userName} earned a certificate in ${courseTitle}`,
    twitterDescription: `Congratulations to ${userName} for completing ${courseTitle} at Neo Prism Academy!`,
    twitterImage: `${baseUrl}/certificate-og-image.png`
  };
};

export const generateCourseMeta = (
  courseTitle: string,
  courseDescription: string,
  courseSlug: string
): MetaTagData => {
  const baseUrl = 'https://neoprismacademy.com';
  
  return {
    title: `${courseTitle} | Neo Prism Academy`,
    description: courseDescription,
    ogTitle: `Learn ${courseTitle} at Neo Prism Academy`,
    ogDescription: courseDescription,
    ogImage: `${baseUrl}/course-og-image.png`,
    ogUrl: `${baseUrl}/course/${courseSlug}`,
    twitterTitle: `Learn ${courseTitle} at Neo Prism Academy`,
    twitterDescription: courseDescription,
    twitterImage: `${baseUrl}/course-og-image.png`
  };
};